import { useState, useEffect } from "react";
import axios from "axios";
import { signInWithGoogle } from "@/lib/firebase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {}, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        username,
        password,
      });
      sessionStorage.setItem("access_token", res.data.access_token);
      window.location.href = "/";
    } catch {
      setError("Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError("");
    try {
      const token = await signInWithGoogle(BACKEND_URL);
      sessionStorage.setItem("access_token", token);
      window.location.href = "/";
    } catch (err) {
      const msg = err.message ?? "";
      if (msg.includes("popup-closed") || msg.includes("cancelled")) {
        setError("Connexion annulée.");
      } else if (msg.includes("non autorisé") || msg.includes("not_authorized")) {
        setError("Accès non autorisé. Contactez l'administrateur pour obtenir un accès.");
      } else {
        setError(msg || "Erreur de connexion Google. Réessayez.");
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-md space-y-5">

        {/* Header */}
        <div className="text-center space-y-1.5">
          <h1 className="text-3xl font-bold text-slate-800">GeriaScan</h1>
          <p className="text-sm text-slate-500">Outil documentaire d'aide à la révision médicamenteuse</p>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Accès professionnel uniquement</p>
        </div>

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Bouton Google */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={googleLoading}
          className="w-full flex items-center justify-center gap-3 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 py-2.5 px-4 rounded-lg font-medium shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          {googleLoading ? "Connexion en cours..." : "Continuer avec Google"}
        </button>

        {/* Séparateur */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-3 text-slate-400">ou</span>
          </div>
        </div>

        {/* Formulaire identifiant/mot de passe */}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Identifiant
            </label>
            <input
              className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              type="text"
              placeholder="votre.nom"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Mot de passe
            </label>
            <input
              className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold shadow-sm transition-colors ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Connexion en cours..." : "Se connecter"}
          </button>
        </form>

        {/* Disclaimer MDR */}
        <div className="pt-2 border-t border-slate-100">
          <p className="text-[10px] leading-relaxed text-slate-400 text-center italic">
            GeriaScan est un outil documentaire. Toute décision thérapeutique relève de la seule responsabilité du praticien qualifié.
          </p>
        </div>
      </div>
    </div>
  );
}
